import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiTrendingUp, FiShoppingCart, FiPackage, FiXCircle,
  FiPlus, FiRefreshCw, FiAlertTriangle,
} from 'react-icons/fi';
import { Sparkline, TrendLineChart } from './DashboardCharts';
import '../../styles/Dashboard.css';

const fmtS = n => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = n => parseFloat(n || 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
const fmtQ = n => parseFloat(n || 0).toLocaleString('es-PE', { maximumFractionDigits: 2 });
const fmtPct = n => `${n >= 0 ? '↑' : '↓'}${Math.abs(n).toFixed(1)}%`;

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function groupByWarehouse(rows) {
  const map = {};
  rows.forEach(r => {
    if (!map[r.warehouse_id]) map[r.warehouse_id] = { warehouse_id: r.warehouse_id, warehouse_name: r.warehouse_name, items: [] };
    map[r.warehouse_id].items.push(r);
  });
  return Object.values(map).sort((a, b) => b.items.length - a.items.length);
}

function KpiCard({ icon, label, value, sub, pctChange, color, sparkline }) {
  const colors = {
    blue: '#3b82f6', purple: '#7c3aed', green: '#16a34a', red: '#dc2626',
  };
  const c = colors[color] || colors.blue;
  const positive = pctChange >= 0;
  return (
    <div className="dsh-kpi-card2">
      <div className="dsh-kpi-card2-top">
        <div>
          <div className="dsh-kpi-card2-label">{label}</div>
          <div className="dsh-kpi-card2-value">{value}</div>
        </div>
        <div className="dsh-kpi-card2-icon" style={{ color: c, background: `${c}18` }}>{icon}</div>
      </div>
      <div className="dsh-kpi-card2-sub">
        <span className={positive ? 'dsh-pct-up' : 'dsh-pct-down'}>{fmtPct(pctChange)}</span>
        <span className="dsh-kpi-card2-subtext">{sub}</span>
      </div>
      <Sparkline data={sparkline} color={c} />
    </div>
  );
}

export default function AdminDashboard({ data, period, date, onPeriodChange, onRefresh, lastUpdated, loading }) {
  const navigate = useNavigate();
  const { kpis, movimientosEfectivo, cobroClientes, pagoProveedores, ingresosDiarios, ingresosMensual, resumenRapido, stockCritico } = data;

  const daysCount = Math.max(ingresosDiarios.actual.length, ingresosDiarios.pasado.length);
  const dayLabels = Array.from({ length: daysCount }, (_, i) => String(i + 1));
  const diariosSeries = [
    { label: `Mes Actual (${MESES[ingresosDiarios.labelMesActual - 1]})`, data: dayLabels.map((_, i) => ingresosDiarios.actual[i] ?? null), color: '#f97316' },
    { label: `Mes Pasado (${MESES[ingresosDiarios.labelMesPasado - 1]})`, data: dayLabels.map((_, i) => ingresosDiarios.pasado[i] ?? null), color: '#0ea5e9' },
  ];
  const mensualSeries = [
    { label: 'Año Actual', data: ingresosMensual.actual, color: '#f97316' },
    { label: 'Año Pasado', data: ingresosMensual.pasado, color: '#dc2626' },
  ];

  const minutesAgo = lastUpdated ? Math.max(0, Math.round((Date.now() - lastUpdated) / 60000)) : 0;
  const todayLabel = new Date().toLocaleDateString('es-PE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', timeZone: 'America/Lima' });

  return (
    <div className="dsh-container">
      <div className="dsh-header">
        <div>
          <h1 className="dsh-title">Resumen del día</h1>
          <p className="dsh-subtitle">
            {todayLabel.charAt(0).toUpperCase() + todayLabel.slice(1)}
            {' · '}Actualizado {minutesAgo === 0 ? 'ahora' : `hace ${minutesAgo} min`}
          </p>
        </div>
        <div className="dsh-header-actions">
          <div className="dsh-period-tabs">
            {[['today', 'Hoy'], ['week', 'Semana'], ['month', 'Mes'], ['year', 'Año']].map(([val, label]) => (
              <button key={val} className={`dsh-period-tab ${period === val ? 'active' : ''}`}
                onClick={() => onPeriodChange(val, date)}>{label}</button>
            ))}
          </div>
          <input type="date" className="dsh-date-input" value={date}
            onChange={e => onPeriodChange(period, e.target.value)} />
          <button className="dsh-btn-secondary" onClick={onRefresh} disabled={loading}>
            <FiRefreshCw size={13} className={loading ? 'dsh-spin' : ''} /> Actualizar
          </button>
          <button className="dsh-btn-primary" onClick={() => navigate('/ventas/nueva')}>
            <FiPlus size={14} /> Nueva venta
          </button>
        </div>
      </div>

      <div className="dsh-kpi-grid dsh-kpi-grid-4">
        <KpiCard icon={<FiTrendingUp />} color="blue" label="TOTAL VENTAS"
          value={fmtS(kpis.ventas.total)} sub={`Nº ventas: ${kpis.ventas.count}`}
          pctChange={kpis.ventas.pct} sparkline={kpis.ventas.sparkline} />
        <KpiCard icon={<FiShoppingCart />} color="purple" label="TOTAL COMPRAS"
          value={fmtS(kpis.compras.total)} sub={`Nº compras: ${kpis.compras.count}`}
          pctChange={kpis.compras.pct} sparkline={kpis.compras.sparkline} />
        <KpiCard icon={<FiPackage />} color="green" label="PRODUCTOS VENDIDOS"
          value={fmtN(kpis.productosVendidos.qty)} sub={`Stock total: ${fmtN(kpis.productosVendidos.stockTotal)} unidades`}
          pctChange={0} sparkline={kpis.productosVendidos.sparkline} />
        <KpiCard icon={<FiXCircle />} color="red" label="VENTAS ANULADAS"
          value={fmtS(kpis.ventasAnuladas.total)} sub={`Nº ventas anuladas: ${kpis.ventasAnuladas.count}`}
          pctChange={0} sparkline={kpis.ventasAnuladas.sparkline} />
      </div>

      <section className="dsh-section">
        <div className="dsh-section-title">
          <span className="dsh-dot" /> VENTAS: CONTADO VS CRÉDITO
          <span className="dsh-section-total">Total: {fmtS(kpis.ventas.total)}</span>
        </div>
        <div className="dsh-payment-grid dsh-payment-grid-2col">
          <div className="dsh-payment-card2" style={{ borderLeft: '3px solid #16a34a' }}>
            <div className="dsh-payment-card2-top">
              <span className="dsh-payment-name">Venta Contado</span>
              <span className="dsh-payment-amount">{fmtS(kpis.ventasContado.total)}</span>
            </div>
            <span className="dsh-payment-count">{kpis.ventasContado.count} transacc.</span>
          </div>
          <div className="dsh-payment-card2" style={{ borderLeft: '3px solid #c2410c' }}>
            <div className="dsh-payment-card2-top">
              <span className="dsh-payment-name">Venta Crédito</span>
              <span className="dsh-payment-amount">{fmtS(kpis.ventasCredito.total)}</span>
            </div>
            <span className="dsh-payment-count">{kpis.ventasCredito.count} transacc.</span>
          </div>
        </div>
      </section>

      <section className="dsh-section">
        <div className="dsh-section-title"><span className="dsh-dot" /> OTROS MOVIMIENTOS DE EFECTIVO</div>
        <div className="dsh-payment-grid dsh-payment-grid-2col">
          <div className="dsh-payment-card2">
            <div className="dsh-payment-card2-top">
              <span className="dsh-payment-name">Ingresos efectivo</span>
              <span className="dsh-payment-amount">{fmtS(movimientosEfectivo.ingreso.total)}</span>
            </div>
            <span className="dsh-payment-count">{movimientosEfectivo.ingreso.count} transacc.</span>
          </div>
          <div className="dsh-payment-card2">
            <div className="dsh-payment-card2-top">
              <span className="dsh-payment-name">Salida efectivo</span>
              <span className="dsh-payment-amount">{fmtS(movimientosEfectivo.salida.total)}</span>
            </div>
            <span className="dsh-payment-count">{movimientosEfectivo.salida.count} transacc.</span>
          </div>
        </div>
      </section>

      <div className="dsh-two-col">
        <section className="dsh-section">
          <div className="dsh-section-title"><span className="dsh-dot" /> TOTAL COBRO A CLIENTES — CUENTAS POR COBRAR</div>
          <div className="dsh-table-wrap">
            <table className="dsh-table">
              <thead><tr><th>Forma de Pago</th><th style={{ textAlign: 'center' }}>Movimientos</th><th style={{ textAlign: 'right' }}>Sub Total</th></tr></thead>
              <tbody>
                {cobroClientes.rows.length === 0 ? (
                  <tr><td colSpan={3} className="dsh-empty">Sin cobros en el período</td></tr>
                ) : cobroClientes.rows.map(r => (
                  <tr key={r.method}>
                    <td>{r.method}</td>
                    <td style={{ textAlign: 'center' }}><span className="dsh-count-badge">{r.transactions}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtS(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dsh-total-badge-row">
            <span>Total Crédito Cobrado</span>
            <span className="dsh-total-badge">{fmtS(cobroClientes.total)}</span>
          </div>
        </section>

        <section className="dsh-section">
          <div className="dsh-section-title"><span className="dsh-dot" /> TOTAL PAGO A PROVEEDORES — CUENTAS POR PAGAR</div>
          <div className="dsh-table-wrap">
            <table className="dsh-table">
              <thead><tr><th>Forma de Pago</th><th style={{ textAlign: 'center' }}>Movimientos</th><th style={{ textAlign: 'right' }}>Sub Total</th></tr></thead>
              <tbody>
                {pagoProveedores.rows.length === 0 ? (
                  <tr><td colSpan={3} className="dsh-empty">Sin pagos en el período</td></tr>
                ) : pagoProveedores.rows.map(r => (
                  <tr key={r.method}>
                    <td>{r.method}</td>
                    <td style={{ textAlign: 'center' }}><span className="dsh-count-badge">{r.transactions}</span></td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtS(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="dsh-total-badge-row">
            <span>Total Crédito Pagado</span>
            <span className="dsh-total-badge">{fmtS(pagoProveedores.total)}</span>
          </div>
        </section>
      </div>

      <div className="dsh-two-col dsh-two-col-wide">
        <section className="dsh-section">
          <div className="dsh-section-title">
            <span className="dsh-dot" /> INGRESOS DIARIOS
            <span className="dsh-section-total">{MESES[ingresosDiarios.labelMesActual - 1]} vs {MESES[ingresosDiarios.labelMesPasado - 1]}</span>
          </div>
          <TrendLineChart labels={dayLabels} series={diariosSeries} />
        </section>

        <section className="dsh-section dsh-resumen-rapido">
          <div className="dsh-section-title"><span className="dsh-dot" /> RESUMEN RÁPIDO</div>
          <div className="dsh-quick-list">
            <div className="dsh-quick-item">
              <span className="dsh-quick-label">Ticket Promedio</span>
              <span className="dsh-quick-value">{fmtS(resumenRapido.ticketPromedio.value)}</span>
              <span className={resumenRapido.ticketPromedio.pct >= 0 ? 'dsh-pct-up' : 'dsh-pct-down'}>{fmtPct(resumenRapido.ticketPromedio.pct)}</span>
            </div>
            <div className="dsh-quick-item">
              <span className="dsh-quick-label">Margen Estimado</span>
              <span className="dsh-quick-value">{resumenRapido.margenEstimado.value.toFixed(1)}%</span>
            </div>
            <div className="dsh-quick-item">
              <span className="dsh-quick-label">Productos Activos</span>
              <span className="dsh-quick-value">{resumenRapido.productosActivos.value} SKU</span>
              {resumenRapido.productosActivos.delta > 0 && <span className="dsh-pct-up">+{resumenRapido.productosActivos.delta}</span>}
            </div>
            <div className="dsh-quick-item">
              <span className="dsh-quick-label">Clientes con Deuda</span>
              <span className="dsh-quick-value">{resumenRapido.clientesConDeuda.value}</span>
            </div>
            <div className="dsh-quick-item">
              <span className="dsh-quick-label">Stock Bajo Mínimo</span>
              <span className="dsh-quick-value">{resumenRapido.stockBajoMinimo.value} items</span>
              {resumenRapido.stockBajoMinimo.value > 0 && <span className="dsh-tag-atencion">atención</span>}
            </div>
          </div>
        </section>
      </div>

      <section className="dsh-section">
        <div className="dsh-section-title">
          <span className="dsh-dot" /> INGRESOS MENSUAL · AÑO ACTUAL VS AÑO PASADO
        </div>
        <TrendLineChart labels={MESES} series={mensualSeries} />
      </section>

      <section className="dsh-section">
        <div className="dsh-section-title">
          <FiAlertTriangle size={12} style={{ color: '#dc2626' }} /> PRODUCTOS EN STOCK MÍNIMO
          <span className="dsh-section-total">{stockCritico.length} producto{stockCritico.length !== 1 ? 's' : ''}</span>
        </div>
        {stockCritico.length === 0 ? (
          <div className="dsh-empty">Sin productos en stock crítico</div>
        ) : groupByWarehouse(stockCritico).map(group => (
          <div key={group.warehouse_id} className="dsh-wh-group">
            <div className="dsh-wh-group-title">
              {group.warehouse_name}
              <span className="dsh-count-badge">{group.items.length}</span>
            </div>
            <div className="dsh-table-wrap">
              <table className="dsh-table">
                <thead>
                  <tr>
                    <th>SKU</th><th>Producto</th><th>Familia</th><th>U.M.</th>
                    <th style={{ textAlign: 'right' }}>Stock</th>
                    <th style={{ textAlign: 'right' }}>Mínimo</th>
                    <th style={{ textAlign: 'right' }}>Déficit</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map(p => (
                    <tr key={`${p.id}-${p.warehouse_id}`}>
                      <td className="dsh-mono">{p.sku}</td>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.family || '—'}</td>
                      <td>{p.unit_measure || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{fmtQ(p.stock)}</td>
                      <td style={{ textAlign: 'right' }}>{fmtQ(p.min_stock)}</td>
                      <td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtQ(p.deficit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
